import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, categoryName, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FIELD = "h-10 border-forest-700/20 bg-white";

const EMPTY = {
  title: "", program: "", amount_label: "", summary: "", bullets: "",
  categories: [], owner_required: true, property_value_cap: "", income_means_test: false,
  min_system_age_years: 0, new_build_eligible: true, sort_order: 0, active: true,
};

export default function AdminRebates() {
  const [rebates, setRebates] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => api.get("/rebates").then((d) => setRebates(d.data)).catch((e) => {
    toast.error(formatApiErrorDetail(e.response?.data?.detail));
    setRebates([]);
  });

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      title: r.title, program: r.program || "", amount_label: r.amount_label || "",
      summary: r.summary || "", bullets: (r.bullets || []).join("\n"),
      categories: r.categories || [], owner_required: !!r.rules?.owner_required,
      property_value_cap: r.rules?.property_value_cap ?? "",
      income_means_test: !!r.rules?.income_means_test,
      min_system_age_years: r.rules?.min_system_age_years || 0,
      new_build_eligible: r.rules?.new_build_eligible !== false,
      sort_order: r.sort_order || 0, active: !!r.active,
    });
    setOpen(true);
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleCategory = (slug) => {
    set("categories", form.categories.includes(slug)
      ? form.categories.filter((c) => c !== slug)
      : [...form.categories, slug]);
  };

  const save = async () => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title, program: form.program, amount_label: form.amount_label,
      summary: form.summary,
      bullets: form.bullets.split("\n").map((s) => s.trim()).filter(Boolean),
      categories: form.categories,
      rules: {
        owner_required: form.owner_required,
        property_value_cap: form.property_value_cap === "" ? null : Number(form.property_value_cap),
        income_means_test: form.income_means_test,
        min_system_age_years: Number(form.min_system_age_years) || 0,
        new_build_eligible: form.new_build_eligible,
      },
      sort_order: Number(form.sort_order) || 0,
      active: form.active,
    };
    try {
      if (editId) await api.put(`/admin/rebates/${editId}`, payload);
      else await api.post("/admin/rebates", payload);
      setOpen(false);
      toast.success(editId ? "Rebate updated" : "Rebate added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/rebates/${deleteTarget.id}`);
      toast.success("Rebate deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div data-testid="admin-rebates">
      <div className="mb-4 flex items-center gap-3">
        <span className="text-xs font-bold text-moss" data-testid="rebates-count">
          {rebates === null ? "Loading…" : `${rebates.length} program${rebates.length === 1 ? "" : "s"}`}
        </span>
        <Button onClick={openNew} data-testid="admin-add-rebate" className="ml-auto gap-2 rounded-full bg-terra hover:bg-terra-dark">
          <Plus className="h-4 w-4" /> Add rebate
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-forest-700/10 bg-white">
        <Table data-testid="rebates-table">
          <TableHeader>
            <TableRow>
              <TableHead>Program</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Applies to</TableHead>
              <TableHead className="w-20">Live</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rebates === null ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-forest-700" />
                </TableCell>
              </TableRow>
            ) : rebates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-sm text-moss" data-testid="rebates-empty">
                  No rebate programs yet — add the first one.
                </TableCell>
              </TableRow>
            ) : (
              rebates.map((r) => (
                <TableRow key={r.id} data-testid={`rebate-row-${r.id}`}>
                  <TableCell className="text-xs font-bold uppercase tracking-[0.08em] text-moss">{r.program}</TableCell>
                  <TableCell className="font-semibold text-forest-700">{r.title}</TableCell>
                  <TableCell className="font-bold text-terra-dark">{r.amount_label}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.categories.map((c) => (
                        <Badge key={c} variant="outline" className="border-forest-700/20 text-[10px] text-forest-700">
                          {categoryName(c)}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-bold ${r.active ? "text-forest-600" : "text-red-500"}`}>
                      {r.active ? "Live" : "Hidden"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      <Button variant="outline" size="icon" className="h-8 w-8 border-forest-700/20" onClick={() => openEdit(r)} data-testid={`rebate-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8 border-red-200 text-red-500" onClick={() => setDeleteTarget(r)} data-testid={`rebate-delete-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl" data-testid="rebate-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-forest-700">{editId ? "Edit rebate" : "Add rebate"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Title *</Label>
                <Input className={FIELD} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="rebate-form-title" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Program</Label>
                <Input className={FIELD} value={form.program} onChange={(e) => set("program", e.target.value)} data-testid="rebate-form-program" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Amount label</Label>
                <Input className={FIELD} value={form.amount_label} onChange={(e) => set("amount_label", e.target.value)} placeholder="Up to $1,400" data-testid="rebate-form-amount" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Sort order</Label>
                <Input type="number" className={FIELD} value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} data-testid="rebate-form-sort" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Summary</Label>
              <Textarea rows={2} value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="rebate-form-summary" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Bullets (one per line)</Label>
              <Textarea rows={3} value={form.bullets} onChange={(e) => set("bullets", e.target.value)} data-testid="rebate-form-bullets" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.1em] text-forest-700">Applies to categories</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {CATEGORIES.map((c) => (
                  <label key={c.slug} className="flex items-center gap-2 text-sm text-forest-700">
                    <input
                      type="checkbox"
                      checked={form.categories.includes(c.slug)}
                      onChange={() => toggleCategory(c.slug)}
                      className="h-4 w-4 accent-[#F0A030]"
                      data-testid={`rebate-form-category-${c.slug}`}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="rounded-md border border-forest-700/10 bg-[#F3F4F1] p-4">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-forest-700">Eligibility rules (drives the checker)</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="flex items-center gap-2.5 text-sm text-forest-700">
                  <Switch checked={form.owner_required} onCheckedChange={(v) => set("owner_required", v)} data-testid="rebate-form-owner-required" />
                  Owner required
                </label>
                <label className="flex items-center gap-2.5 text-sm text-forest-700">
                  <Switch checked={form.income_means_test} onCheckedChange={(v) => set("income_means_test", v)} data-testid="rebate-form-income-test" />
                  Income means test
                </label>
                <label className="flex items-center gap-2.5 text-sm text-forest-700">
                  <Switch checked={form.new_build_eligible} onCheckedChange={(v) => set("new_build_eligible", v)} data-testid="rebate-form-new-build" />
                  New builds eligible
                </label>
                <div>
                  <Label className="text-xs text-moss">Property value cap ($)</Label>
                  <Input type="number" className={FIELD} value={form.property_value_cap} onChange={(e) => set("property_value_cap", e.target.value)} placeholder="3000000 or empty" data-testid="rebate-form-cap" />
                </div>
                <div>
                  <Label className="text-xs text-moss">Min. system age (years)</Label>
                  <Input type="number" className={FIELD} value={form.min_system_age_years} onChange={(e) => set("min_system_age_years", e.target.value)} data-testid="rebate-form-min-age" />
                </div>
                <label className="flex items-center gap-2.5 text-sm text-forest-700 sm:col-span-2">
                  <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="rebate-form-active" />
                  Live on public site
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-forest-700/10 pt-4">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="rebate-form-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="gap-2 rounded-full bg-forest-700 hover:bg-forest-600" data-testid="rebate-form-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {editId ? "Save changes" : "Add rebate"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="rebate-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              The program will disappear from the public rebates page and the eligibility checker immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rebate-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="rebate-delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
